import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  Upload, 
  X, 
  FileText,
  Mail,
  CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for uploaded assets
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API}${url.startsWith('/api') ? '' : '/api'}${url}`;
  return `${API}/api/${url}`;
};

// Brand Logo component that uses uploaded logo
const BrandLogo = ({ logoUrl, dark = true }) => (
  <div className="flex items-center gap-2">
    <img src={logoUrl} alt="MedStudyPro" className="h-8 object-contain" />
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Default MedStudyPro Logo Component - Updated geometric M design
const DefaultLogo = ({ dark = true }) => (
  <div className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left M leg */}
      <path d="M4 32V10L12 18L20 10" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Right M leg */}
      <path d="M20 10L28 18L36 10V32" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Center cube - top face */}
      <path d="M20 16L24 18V22L20 24L16 22V18L20 16Z" fill="#334155"/>
      {/* Center cube - visible faces */}
      <path d="M20 16L24 18L20 20L16 18L20 16Z" fill="#475569"/>
      <path d="M20 20L24 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.6"/>
      <path d="M20 20L16 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.4"/>
    </svg>
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

export default function Contact() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(null);
  
  const [settings, setSettings] = useState({
    form_title: 'Contact Us',
    form_intro: 'Have a question or feedback? We\'d love to hear from you.',
    subject_options: ['Bug Report', 'Content Feedback', 'Account/Subscription', 'General Question'],
    enable_file_upload: true,
    max_file_size_mb: 10,
    max_files: 3,
    success_message: 'Thanks — we received your message and will respond as soon as possible.'
  });
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const res = await fetch(`${API}/api/logo-settings`);
      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.logo_url || data.logo_light_url);
      }
    } catch (err) {
      console.error('Failed to fetch logo:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/contact/settings`);
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch contact settings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to render the logo
  const renderLogo = (dark = true) => {
    if (logoUrl) {
      return <BrandLogo logoUrl={getFullUrl(logoUrl)} dark={dark} />;
    }
    return <DefaultLogo dark={dark} />;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = settings.max_files - uploadedFiles.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${settings.max_files} files allowed`);
      return;
    }
    
    const filesToUpload = files.slice(0, remainingSlots);
    
    setUploading(true);
    
    for (const file of filesToUpload) {
      const maxSize = settings.max_file_size_mb * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is ${settings.max_file_size_mb}MB`);
        continue;
      }
      
      try {
        const uploadData = new FormData();
        uploadData.append('file', file);
        
        const res = await fetch(`${API}/api/contact/upload-file`, {
          method: 'POST',
          body: uploadData
        });
        
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Upload failed');
        }
        
        const data = await res.json();
        setUploadedFiles(prev => [...prev, data]);
        
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.full_name);
      submitData.append('email', formData.email);
      submitData.append('subject', formData.subject || 'General Question');
      submitData.append('message', formData.message);
      submitData.append('file_urls', JSON.stringify(uploadedFiles));
      
      const res = await fetch(`${API}/api/contact/submit`, {
        method: 'POST',
        body: submitData
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Submission failed');
      }
      
      setSubmitted(true);
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#192A3F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#192A3F]">
        <nav className="max-w-7xl mx-auto px-6 py-5">
          <button onClick={() => navigate('/')}>
            {renderLogo(true)}
          </button>
        </nav>
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-white rounded-2xl p-12 text-center shadow-xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1E3A5C] mb-4">Message Sent!</h2>
            <p className="text-slate-600 mb-6">
              {settings.success_message}
            </p>
            <Button onClick={() => navigate('/')} className="bg-[#FF9E00] hover:bg-[#E88D00] text-white rounded-full px-8">
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#192A3F]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <button onClick={() => navigate('/')}>
          {renderLogo(true)}
        </button>
        <div className="flex items-center gap-6">
          <button 
            onClick={() => navigate('/about')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            About
          </button>
          <button 
            onClick={() => navigate('/pricing')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            Pricing
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div className="max-w-2xl mx-auto px-6 py-8 text-center">
        <div className="inline-flex items-center gap-2 p-4 bg-[#264B73] rounded-2xl mb-6">
          <Mail className="w-8 h-8 text-[#FF9E00]" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">{settings.form_title}</h1>
        <p className="text-white/60">{settings.form_intro}</p>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 pb-16">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <Label htmlFor="full_name" className="text-[#1E3A5C] font-medium">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))}
                placeholder="Your full name"
                className={`mt-1.5 h-12 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl ${errors.full_name ? 'border-red-500' : ''}`}
                data-testid="contact-name-input"
              />
              {errors.full_name && (
                <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>
              )}
            </div>
            
            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-[#1E3A5C] font-medium">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="your@email.com"
                className={`mt-1.5 h-12 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                data-testid="contact-email-input"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            
            {/* Subject */}
            <div>
              <Label htmlFor="subject" className="text-[#1E3A5C] font-medium">Subject</Label>
              <Select
                value={formData.subject || 'none'}
                onValueChange={(v) => setFormData(p => ({ ...p, subject: v === 'none' ? '' : v }))}
              >
                <SelectTrigger className="mt-1.5 h-12 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl">
                  <SelectValue placeholder="Select a subject (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a subject (optional)</SelectItem>
                  {(settings.subject_options || []).map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-[#1E3A5C] font-medium">
                Message <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
                placeholder="How can we help you?"
                rows={5}
                className={`mt-1.5 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl ${errors.message ? 'border-red-500' : ''}`}
                data-testid="contact-message-input"
              />
              {errors.message && (
                <p className="text-red-500 text-sm mt-1">{errors.message}</p>
              )}
            </div>
            
            {/* File Upload */}
            {settings.enable_file_upload && (
              <div>
                <Label className="text-[#1E3A5C] font-medium">
                  Attachments (optional)
                </Label>
                <p className="text-sm text-slate-500 mb-2">
                  Upload up to {settings.max_files} files (max {settings.max_file_size_mb}MB each)
                </p>
                
                {/* Uploaded Files */}
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-[#F8FAFD] rounded-xl">
                        <FileText className="w-5 h-5 text-[#FF9E00]" />
                        <span className="flex-1 text-sm text-[#1E3A5C] truncate">{file.original_name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {uploadedFiles.length < settings.max_files && (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full h-12 border-dashed border-2 border-[#E8EEF3] bg-[#F8FAFD] hover:bg-[#E8EEF3] text-[#1E3A5C] rounded-xl"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Files
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#FF9E00] hover:bg-[#E88D00] text-white rounded-full font-semibold shadow-lg shadow-[#FF9E00]/20"
              data-testid="contact-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0D1520] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {renderLogo(true)}
            <div className="flex items-center gap-8">
              <button onClick={() => navigate('/')} className="text-white/60 hover:text-white text-sm">Home</button>
              <button onClick={() => navigate('/about')} className="text-white/60 hover:text-white text-sm">About</button>
              <button onClick={() => navigate('/pricing')} className="text-white/60 hover:text-white text-sm">Pricing</button>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">© 2026 MedStudyPro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
